import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './RunDetail.css';

const RunDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  usePageTitle(run ? `Run ${run.run_id}` : 'Run Details');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logFilter, setLogFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchRunDetails = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/etl/runs/${id}/`);
      setRun(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load run details');
      setLoading(false);
    }
  }, [id]);

  const fetchLogs = useCallback(async () => {
    try {
      const params = logFilter !== 'all' ? { level: logFilter } : {};
      const response = await axiosInstance.get(`/etl/runs/${id}/logs/`, { params });
      setLogs(response.data);
    } catch (err) {
      console.error('Failed to load logs:', err);
    }
  }, [id, logFilter]);

  useEffect(() => {
    fetchRunDetails();
    fetchLogs();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchRunDetails();
        fetchLogs();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [id, autoRefresh, fetchRunDetails, fetchLogs]);

  const cancelRun = async () => {
    if (!confirm('Are you sure you want to cancel this run?')) return;

    try {
      await axiosInstance.post(`/etl/runs/${id}/cancel/`);
      alert('Run cancelled successfully!');
      fetchRunDetails();
    } catch (err) {
      alert('Failed to cancel run: ' + (err.response?.data?.error || err.message));
    }
  };

  const retryRun = async () => {
    if (!confirm(`Retry flow "${run.flow_name}"?`)) return;

    try {
      await axiosInstance.post(`/etl/flows/${run.flow}/trigger/`);
      alert('Flow triggered successfully!');
      navigate('/runs');
    } catch (err) {
      alert('Failed to retry: ' + (err.response?.data?.error || err.message));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'warning', label: 'Pending' },
      extracting: { class: 'info', label: 'Extracting' },
      uploading: { class: 'info', label: 'Uploading' },
      processing: { class: 'info', label: 'Processing' },
      loading: { class: 'info', label: 'Loading' },
      completed: { class: 'success', label: 'Completed' },
      failed: { class: 'error', label: 'Failed' },
      cancelled: { class: 'secondary', label: 'Cancelled' },
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const getLogLevelColor = (level) => {
    const colors = {
      DEBUG: 'log-debug',
      INFO: 'log-info',
      WARNING: 'log-warning',
      ERROR: 'log-error',
      CRITICAL: 'log-critical',
    };
    return colors[level] || 'log-info';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const isRunning = (status) => {
    return ['pending', 'extracting', 'uploading', 'processing', 'loading'].includes(status);
  };

  if (loading) {
    return (
      <div className="run-detail-container">
        <div className="loading">Loading run details...</div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="run-detail-container">
        <div className="error">{error || 'Run not found'}</div>
        <button onClick={() => navigate('/runs')} className="btn-primary">
          Back to Runs
        </button>
      </div>
    );
  }

  const statusConfig = getStatusBadge(run.status);

  return (
    <div className="run-detail-container">
      {/* Compact Header */}
      <div className="detail-header">
        <div className="header-left">
          <button onClick={() => navigate('/runs')} className="btn-back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Runs
          </button>
          <span className="header-separator">/</span>
          <code className="run-id-header">{run.run_id}</code>
          <span className={`status-badge-sm badge-${statusConfig.class}`}>
            {statusConfig.label}
          </span>
        </div>
        <div className="header-right">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          {(run.status === 'failed' || run.status === 'cancelled') && (
            <button onClick={retryRun} className="btn-retry">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Retry
            </button>
          )}
          {isRunning(run.status) && (
            <button onClick={cancelRun} className="btn-cancel-run">
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Compact Info Row */}
      <div className="info-row">
        <div className="info-row-main">
          <span
            className="flow-link"
            onClick={() => navigate(`/flows/${run.flow}`)}
          >
            {run.flow_name}
          </span>
          <span className="info-separator">•</span>
          <span className="info-item">
            <span className="info-label">Started</span>
            <span className="info-value">{formatDate(run.started_at)}</span>
          </span>
          <span className="info-separator">•</span>
          <span className="info-item">
            <span className="info-label">Duration</span>
            <span className="info-value">
              {isRunning(run.status) ? 'Running...' : formatDuration(run.duration_seconds)}
            </span>
          </span>
          <span className="info-separator">•</span>
          <span className="info-item">
            <span className="info-label">By</span>
            <span className="info-value">{run.triggered_by_username || 'System'}</span>
          </span>
        </div>
      </div>

      {/* Compact Stats Row */}
      <div className="stats-row">
        <div className="stat-inline">
          <span className="stat-number">{run.records_extracted || 0}</span>
          <span className="stat-label">Extracted</span>
        </div>
        <div className="stat-inline">
          <span className="stat-number success">{run.records_loaded || 0}</span>
          <span className="stat-label">Loaded</span>
        </div>
        <div className="stat-inline">
          <span className="stat-number error">{run.records_failed || 0}</span>
          <span className="stat-label">Failed</span>
        </div>
        {run.dataflow_job_id && (
          <div className="stat-inline wide">
            <span className="stat-label">Dataflow</span>
            <code className="stat-code">{run.dataflow_job_id}</code>
          </div>
        )}
      </div>

      {/* Error Message - Compact */}
      {run.error_message && (
        <div className="error-banner">
          <div className="error-banner-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>Error</span>
          </div>
          <div className="error-banner-content">{run.error_message}</div>
          {run.error_stack_trace && (
            <details className="stack-trace">
              <summary>View Stack Trace</summary>
              <pre>{run.error_stack_trace}</pre>
            </details>
          )}
        </div>
      )}

      {/* Logs Section */}
      <div className="logs-section">
        <div className="logs-header">
          <div className="logs-title">
            <h2>Logs</h2>
            <span className="log-count">{logs.length}</span>
          </div>
          <div className="log-filters">
            {['all', 'INFO', 'WARNING', 'ERROR'].map(level => (
              <button
                key={level}
                className={`filter-btn ${logFilter === level ? 'active' : ''}`}
                onClick={() => setLogFilter(level)}
              >
                {level === 'all' ? 'All' : level.charAt(0) + level.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="logs-container">
          {logs.length === 0 ? (
            <div className="no-logs">No logs available</div>
          ) : (
            logs.map((log, index) => (
              <div key={index} className={`log-entry ${getLogLevelColor(log.level)}`}>
                <span className="log-time">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                <span className={`log-level level-${log.level.toLowerCase()}`}>
                  {log.level}
                </span>
                <span className="log-msg">{log.message}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RunDetail;
